import type { Background, CanvasData, Mask, Spacing, TextStyle } from '../data'
import { ALL_BORDER_RADIUS_CORNERS, BASE_FLOAT_DEFAULTS, NO_TEXT_HIGHLIGHT } from '../data'

const f = BASE_FLOAT_DEFAULTS

const zeroSpacing: Spacing = {
	paddingLeft: 0,
	paddingRight: 0,
	paddingTop: 0,
	paddingBottom: 0,
	marginLeft: 0,
	marginRight: 0,
	marginTop: 0,
	marginBottom: 0,
}
const transparentBackground: Background = {
	mode: 'transparent',
	color: '#000000',
	imageUrl: '',
	imageScale: 100,
	imageOffsetX: 50,
	imageOffsetY: 50,
}
const defaultMask: Mask = {
	maskMode: 'none',
	blendMode: 'normal',
	solidColor: { mode: 'foregroundColor', color: '#000000' },
	solidOpacity: 50,
	gradientColorStart: { mode: 'foregroundColor', color: '#000000' },
	gradientColorEnd: { mode: 'foregroundColor', color: '#000000' },
	gradientOpacityStart: 100,
	gradientOpacityEnd: 0,
	gradientAngle: 0,
}
const autoTextStyle: TextStyle = {
	textColorMode: 'auto',
	textColor: { mode: 'foregroundColor', color: '#ffffff' },
	fontFamilyMode: 'auto',
	fontFamilyProvider: 'system',
	fontFamilyGoogleFamily: '',
	fontFamilyCustomUrl: '',
	fontFamilyCustomName: '',
	fontSizeMode: 'auto',
	fontSizeUnit: 'px',
	fontSizePxValue: 16,
	fontWeightMode: 'auto',
	fontWeightValue: 400,
	fontStyleMode: 'auto',
	fontStyleValue: 'normal',
	lineHeightMode: 'auto',
	lineHeightValue: 1.4,
	textShadowMode: 'auto',
	textShadowValue: 'simple',
	textShadowSimple: { opacity: 20, spread: 5 },
}

export const systemDefaultTemplate: CanvasData = {
	version: 1,
	layers: {
		'backdrop-bg': {
			type: 'backdrop',
			id: 'backdrop-bg',
			hidden: false,
			locked: false,
			background: {
				mode: 'externalImage',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			mask: {
				...defaultMask,
				maskMode: 'gradient',
				gradientColorStart: { mode: 'color', color: '#000000' },
				gradientOpacityStart: 40,
				gradientOpacityEnd: 0,
				gradientAngle: 0,
			},
		},
		'content-main': {
			type: 'content',
			id: 'content-main',
			hidden: false,
			locked: false,
			xPin: 'left',
			yPin: 'bottom',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: { ...zeroSpacing, marginLeft: 40, marginRight: 80, marginBottom: 40, marginTop: 0 },
			gap: 6,
			textStyle: { ...autoTextStyle, textShadowMode: 'value', textShadowValue: 'simple' },
		},
		'text-title': {
			type: 'text',
			id: 'text-title',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'title',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: zeroSpacing,
			background: transparentBackground,
			textStyle: {
				...autoTextStyle,
				fontSizeMode: 'value',
				fontSizePxValue: 32,
				fontWeightMode: 'value',
				fontWeightValue: 600,
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'text-subtitle': {
			type: 'text',
			id: 'text-subtitle',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'subtitle',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: zeroSpacing,
			background: transparentBackground,
			textStyle: {
				...autoTextStyle,
				fontSizeMode: 'value',
				fontSizePxValue: 20,
				fontWeightMode: 'value',
				fontWeightValue: 400,
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'text-cta': {
			type: 'text',
			id: 'text-cta',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'cta',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				...zeroSpacing,
				paddingLeft: 16,
				paddingRight: 16,
				paddingTop: 8,
				paddingBottom: 8,
				marginTop: 4,
			},
			background: { ...transparentBackground, mode: 'accentBackgroundColor' },
			textStyle: {
				...autoTextStyle,
				textColorMode: 'value',
				textColor: { mode: 'accentForegroundColor', color: '#ffffff' },
				fontSizeMode: 'value',
				fontSizePxValue: 14,
				fontWeightMode: 'value',
				fontWeightValue: 500,
				textShadowMode: 'value',
				textShadowValue: 'none',
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 6,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'content-location': {
			type: 'content',
			id: 'content-location',
			hidden: false,
			locked: false,
			xPin: 'right',
			yPin: 'top',
			textAlignMode: 'auto',
			textAlignValue: 'right',
			spacing: { ...zeroSpacing, marginLeft: 0, marginRight: 40, marginBottom: 0, marginTop: 40 },
			gap: 0,
			textStyle: autoTextStyle,
		},
		'substack-location': {
			type: 'substack',
			id: 'substack-location',
			hidden: false,
			locked: false,
			...f,
			direction: 'row',
			alignItems: 'center',
			justifyContent: 'start',
			childSizing: 'auto',
			widthSizing: 'auto',
			gap: 4,
			fontFamilyMode: 'auto',
			fontFamilyProvider: 'system',
			fontFamilyGoogleFamily: '',
			fontFamilyCustomUrl: '',
			fontFamilyCustomName: '',
			spacing: zeroSpacing,
			background: transparentBackground,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'shape-location-pin': {
			type: 'shape',
			id: 'shape-location-pin',
			hidden: false,
			locked: false,
			...f,
			shapeKind: 'preset',
			preset: 'locationPin',
			fillMode: 'original',
			fill: { mode: 'foregroundColor', color: '#ffffff' },
			shapeFit: 'contain',
			width: { mode: 'px', pxValue: 16, percentValue: 100 },
			height: { mode: 'px', pxValue: 16, percentValue: 100 },
			background: transparentBackground,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
			spacing: zeroSpacing,
		},
		'text-location': {
			type: 'text',
			id: 'text-location',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'location',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: zeroSpacing,
			background: transparentBackground,
			textStyle: { ...autoTextStyle, fontSizeMode: 'value', fontSizePxValue: 15 },
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'content-company-logo': {
			type: 'content',
			id: 'content-company-logo',
			hidden: false,
			locked: false,
			xPin: 'left',
			yPin: 'top',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: { ...zeroSpacing, marginLeft: 40, marginRight: 0, marginBottom: 0, marginTop: 40 },
			gap: 0,
			textStyle: autoTextStyle,
		},
		'image-company-logo': {
			type: 'image',
			id: 'image-company-logo',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'logo',
			customValue: '',
			width: { mode: 'px', pxValue: 64, percentValue: 100 },
			spacing: { ...zeroSpacing, paddingLeft: 12, paddingRight: 12, paddingTop: 12, paddingBottom: 12 },
			background: { ...transparentBackground, mode: 'color', color: '#ffffff' },
			borderRadius: 10,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
			opacity: 100,
		},
		'content-platform-logo': {
			type: 'content',
			id: 'content-platform-logo',
			hidden: false,
			locked: false,
			xPin: 'right',
			yPin: 'bottom',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: { ...zeroSpacing, marginLeft: 0, marginRight: 40, marginBottom: 40, marginTop: 0 },
			gap: 0,
			textStyle: autoTextStyle,
		},
		'image-platform-logo': {
			type: 'image',
			id: 'image-platform-logo',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'platformLogo',
			customValue: '',
			width: { mode: 'px', pxValue: 80, percentValue: 100 },
			spacing: zeroSpacing,
			background: transparentBackground,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
			opacity: 100,
		},
	},
	layerTree: [
		{
			id: 'content-company-logo',
			children: [
				{ id: 'image-company-logo', children: [] },
			],
		},
		{
			id: 'content-main',
			children: [
				{ id: 'text-title', children: [] },
				{ id: 'text-subtitle', children: [] },
				{ id: 'text-cta', children: [] },
			],
		},
		{
			id: 'content-location',
			children: [
				{
					id: 'substack-location',
					children: [
						{ id: 'shape-location-pin', children: [] },
						{ id: 'text-location', children: [] },
					],
				},
			],
		},
		{
			id: 'content-platform-logo',
			children: [
				{ id: 'image-platform-logo', children: [] },
			],
		},
		{ id: 'backdrop-bg', children: [] },
	],
}
